import type { SetlistContext } from './client';
import { MOTION_GRAPHICS_COLLECTION } from './collections';
import { Compatibility } from './compatibility';

import type { MotionGraphics } from './motion-graphics-types';

/**
 * Fetches similar motion graphics based on keywords and categories.
 * Results are ranked by:
 * - Number of matching keywords (when keywords are provided)
 * - Exact keyword matches are prioritized
 * - Number of matching fields (keywords and name)
 * - Token position in the field (earlier matches rank higher)
 *
 * Results can match based on:
 * - When keywords are provided: keywords in either the keywords or name fields
 * - When no keywords are provided: matching style IDs, theme IDs, or type IDs
 *
 * All matches must have:
 * - Either the specified compatibility OR drag-and-drop compatibility (ID: 5)
 * - Not be the current item (filtered by name)
 *
 * @param {Object} params - The search parameters
 * @param {string[]} [params.styles=[]] - Array of style IDs to match against (used only when no keywords are provided)
 * @param {string[]} [params.themes=[]] - Array of theme IDs to match against (used only when no keywords are provided)
 * @param {string[]} [params.types=[]] - Array of type IDs to match against (used only when no keywords are provided)
 * @param {string[]} [params.keywords=[]] - Array of keywords to search and rank by (searches in both keywords and name fields)
 * @param {string} params.name - Name of the current item to exclude from results
 * @param {number} params.page - Page number for pagination
 * @param {string} params.compatibility - Compatibility ID to match against
 * @param {string} [params.additionalFilter] - Additional filter to apply to the results
 * @param {number} [params.resultsPerPage=12] - Number of results per page
 *
 * @returns {Promise<{
 *   data: MotionGraphics[],
 *   hasMore: boolean
 * }>} Object containing the matched motion graphics and pagination info
 */
export const fetchSimilarMotionGraphics = async (
  context: SetlistContext,
  {
    styles = [],
    themes = [],
    types = [],
    keywords = [],
    name,
    page,
    compatibility,
    additionalFilter = '',
    resultsPerPage = 12,
  }: {
    keywords?: string[];
    styles?: string[];
    themes?: string[];
    types?: string[];
    name: string;
    page: number;
    compatibility: string;
    additionalFilter?: string;
    resultsPerPage?: number;
  }
) => {
  // Group filters by category using OR logic within each group
  const styleFilter =
    styles.length > 0
      ? `(${styles.map((style) => `styles.id:=[${String(style)}]`).join(' || ')})`
      : '';

  const themeFilter =
    themes.length > 0
      ? `(${themes.map((theme) => `themes.id:=[${String(theme)}]`).join(' || ')})`
      : '';

  const typeFilter =
    types.length > 0
      ? `(${types.map((type) => `types.id:=[${String(type)}]`).join(' || ')})`
      : '';

  // Combine category filters with OR logic
  const categoryFilters = [styleFilter, themeFilter, typeFilter].filter(
    Boolean
  );

  /*
   * Handle compatibility:
   * - If compatibility is Drag & Drop, we only need that
   * - If compatibility is Premiere Pro or MOGRT, we both, plus Drag & Drop
   * - Otherwise, we want the specified compatibility, plus Drag & Drop
   */
  const compatibilityFilter =
    compatibility === `${Compatibility.DragAndDrop}`
      ? `compatibility.id:=[${Compatibility.DragAndDrop}]`
      : compatibility === `${Compatibility.PremierePro}` ||
          compatibility === `${Compatibility.MOGRT}`
        ? `compatibility.id:=[${Compatibility.PremierePro}] || compatibility.id:=[${Compatibility.MOGRT}] || compatibility.id:=[${Compatibility.DragAndDrop}]`
        : `compatibility.id:=[${compatibility}] || compatibility.id:=[${Compatibility.DragAndDrop}]`;

  // Base filter string (always includes compatibility and name exclusion)
  let filterString =
    categoryFilters.length > 0 && keywords.length === 0
      ? `(${categoryFilters.join(' || ')}) && ${compatibilityFilter} && name:!\`${name}\``
      : `${compatibilityFilter} && name:!\`${name}\``;

  // Add additional filter if provided
  if (additionalFilter) {
    filterString = `${filterString} && ${additionalFilter}`;
  }

  const searchParams = {
    q: keywords.length > 0 ? [name, ...keywords].join(', ') : '*',
    query_by: 'keywords, name',
    filter_by: filterString,
    page,
    per_page: resultsPerPage,
    prioritize_exact_match: true,
    sort_by: keywords.length > 0 ? '_text_match:desc' : undefined,
    num_typos: 0,
    min_len_1typo: 999, // Effectively disable typo tolerance
    min_len_2typo: 999, // Effectively disable typo tolerance
    exhaustive_search: true,
    drop_tokens_threshold: 0,
    prioritize_token_position: true,
    prioritize_num_matching_fields: true,
  };

  const response = await context.client
    .collections(MOTION_GRAPHICS_COLLECTION)
    .documents()
    .search(searchParams, {});

  const { page: currentPage, found, hits } = response;

  let hasMore = false;

  if (hits) {
    hasMore = resultsPerPage * (currentPage - 1) + hits.length < found;
  }

  if (!hits || hits.length === 0) {
    return {
      data: [],
      hasMore: false,
    };
  }

  return {
    data: response?.hits?.map((hit) => hit.document as MotionGraphics) ?? [],
    hasMore,
  };
};
